import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const RestaurantContext = createContext(null);

export function RestaurantProvider({ children }) {
  const { user, refreshUser } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [activeRestaurant, setActiveRestaurant] = useState(null);
  const isCreator = !!user?.creator_account || !!activeRestaurant?.creator_account;

  const refreshRestaurant = useCallback(async () => {
    if (!user?.restaurant_id) {
      setRestaurants([]);
      setActiveRestaurant(null);
      return;
    }
    try {
      const [{ data: list }, { data: cur }] = await Promise.all([
        api.get("/restaurants").catch(() => ({ data: [] })),
        api.get(`/restaurants/${user.restaurant_id}`),
      ]);
      setActiveRestaurant(cur);
      const normalized = list && list.length > 0 ? list : [cur];
      setRestaurants(normalized);
    } catch {
      setActiveRestaurant({ id: user.restaurant_id, name: "My Restaurant" });
      setRestaurants([{ id: user.restaurant_id, name: "My Restaurant" }]);
    }
  }, [user?.restaurant_id]);

  useEffect(() => { refreshRestaurant(); }, [refreshRestaurant]);

  const switchRestaurant = async (rest) => {
    if (!rest || rest.id === user?.restaurant_id) {
      setActiveRestaurant(rest);
      return;
    }
    try {
      const { data } = await api.post(`/restaurants/${rest.id}/switch`);
      if (data.token) {
        localStorage.setItem("token", data.token);
        api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        toast.success(`Switched to ${data.restaurant_name}`);
        if (refreshUser) await refreshUser();
        await refreshRestaurant();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to switch restaurant");
    }
  };

  const createRestaurant = async (name, address = "") => {
    if (!isCreator) {
      toast.error("Multi-restaurant requires Enterprise plan or higher.");
      return null;
    }
    try {
      const { data } = await api.post("/restaurants", { name, address });
      toast.success(`${name} created`);
      await refreshRestaurant();
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create restaurant");
      return null;
    }
  };

  const updateRestaurant = async (restId, payload) => {
    if (!restId) return;
    try {
      const { data } = await api.put(`/restaurants/${restId}`, payload);
      setActiveRestaurant(data);
      setRestaurants((prev) => prev.map((r) => (r.id === restId ? data : r)));
      return data;
    } catch (err) {
      toast.error("Failed to update restaurant");
      throw err;
    }
  };

  const deleteRestaurant = async () => {
    toast.error("Restaurant deletion is disabled. Contact support to remove a location.");
  };

  const value = {
    restId: user?.restaurant_id || null,
    restaurant: activeRestaurant,
    restaurants,
    activeRestaurant,
    isCreator,
    switchRestaurant,
    createRestaurant,
    updateRestaurant,
    deleteRestaurant,
    refreshRestaurant,
  };

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  const ctx = useContext(RestaurantContext);
  if (!ctx) throw new Error("useRestaurant must be used within RestaurantProvider");
  return ctx;
}
